import { useEffect, useState } from 'react';
import { universeApi } from '../lib/api';
import type { DocThumb } from '../lib/docThumb';

/** Renders parse whole files on the main thread; two at a time keeps scrolling smooth. */
const CONCURRENCY = 2;

const results = new Map<string, Promise<DocThumb | null>>();
const queue: Array<() => void> = [];
let running = 0;

function pump() {
  while (running < CONCURRENCY && queue.length) {
    running += 1;
    queue.shift()!();
  }
}

function schedule<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    queue.push(() => {
      job()
        .then(resolve, reject)
        .finally(() => {
          running -= 1;
          pump();
        });
    });
    pump();
  });
}

function thumbFor(id: string): Promise<DocThumb | null> {
  const cached = results.get(id);
  if (cached) return cached;
  const request = schedule(async () => {
    const [asset, { renderDocThumb }] = await Promise.all([universeApi.getAsset(id), import('../lib/docThumb')]);
    return renderDocThumb(asset);
  }).catch((err: unknown) => {
    console.warn('[universe] thumbnail failed', id, err);
    return null;
  });
  results.set(id, request);
  return request;
}

/** First-page image for a document card that came without artwork; null until (or unless) one renders. */
export function useDocThumb(id: string, enabled: boolean): DocThumb | null {
  const [thumb, setThumb] = useState<DocThumb | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    thumbFor(id).then((result) => alive && setThumb(result));
    return () => {
      alive = false;
    };
  }, [id, enabled]);

  return thumb;
}
